import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useCart } from '../../context/CartContext';
import { useAuth } from '../../context/AuthContext';
import { shippingApi } from '../../network/apis/logistics';
import { ordersApi } from '../../network/apis/orders';
import FormInput from '../../components/elements/form_input';
import PhoneInput from '../../components/elements/phone_input';
import Paystack from '@paystack/inline-js';
import { toast } from 'react-toastify';
import { 
    MapPin, Truck, CreditCard, Package, ChevronRight, 
    Add, Check, Clock, Shield, Back, Loader
} from '../../assets/icons';
import prod from '../../assets/images/prod_img.png';
import '../../assets/css/pages/checkout.css';

const NIGERIAN_STATES = [
    'Abia', 'Adamawa', 'Akwa Ibom', 'Anambra', 'Bauchi', 'Bayelsa', 'Benue', 'Borno',
    'Cross River', 'Delta', 'Ebonyi', 'Edo', 'Ekiti', 'Enugu', 'FCT', 'Gombe',
    'Imo', 'Jigawa', 'Kaduna', 'Kano', 'Katsina', 'Kebbi', 'Kogi', 'Kwara',
    'Lagos', 'Nasarawa', 'Niger', 'Ogun', 'Ondo', 'Osun', 'Oyo', 'Plateau',
    'Rivers', 'Sokoto', 'Taraba', 'Yobe', 'Zamfara'
];

const CheckoutPage = () => {
    const navigate = useNavigate();
    const { cartItems, calculateTotal, clearCart } = useCart();
    const { user, isAuthenticated } = useAuth();
    
    // Checkout steps
    const [currentStep, setCurrentStep] = useState(1);
    const STEPS = ['Shipping', 'Delivery', 'Payment', 'Review'];
    
    // Address state
    const [savedAddresses, setSavedAddresses] = useState([]);
    const [selectedAddressId, setSelectedAddressId] = useState(null);
    const [showNewAddressForm, setShowNewAddressForm] = useState(false);
    const [newAddress, setNewAddress] = useState({
        label: 'Home',
        recipient_name: user?.first_name ? `${user.first_name} ${user.last_name}` : '',
        phone: user?.phone || '',
        address: '',
        city: '',
        state: '',
        postal_code: '',
        is_default: false
    });
    
    // Shipping state
    const [shippingRates, setShippingRates] = useState([]);
    const [selectedShipping, setSelectedShipping] = useState(null);
    const [loadingShipping, setLoadingShipping] = useState(false);
    
    // Payment state
    const [paymentMethod, setPaymentMethod] = useState('paystack');
    
    // Order state
    const [isProcessing, setIsProcessing] = useState(false);
    const [orderNotes, setOrderNotes] = useState('');
    
    const subtotal = useMemo(() => calculateTotal(), [calculateTotal]);
    const shippingCost = selectedShipping?.price || 0;
    const total = subtotal + shippingCost;
    
    const selectedAddress = useMemo(() => {
        if (selectedAddressId === 'new') return newAddress;
        return savedAddresses.find(a => a.id === selectedAddressId);
    }, [selectedAddressId, savedAddresses, newAddress]);

    // Redirect if cart is empty
    useEffect(() => {
        if (cartItems.length === 0) {
            navigate('/cart');
        }
    }, [cartItems, navigate]);

    // Redirect if not authenticated
    useEffect(() => {
        if (!isAuthenticated) {
            localStorage.setItem('redirectAfterLogin', '/checkout');
            navigate('/login');
        }
    }, [isAuthenticated, navigate]);

    // Fetch saved addresses
    useEffect(() => {
        const fetchAddresses = async () => {
            try {
                const response = await shippingApi.getAddresses();
                const addresses = response.data?.data || response.data || [];
                setSavedAddresses(addresses);
                
                // Select default address
                const defaultAddr = addresses.find(a => a.is_default);
                if (defaultAddr) {
                    setSelectedAddressId(defaultAddr.id);
                } else if (addresses.length > 0) {
                    setSelectedAddressId(addresses[0].id);
                } else {
                    setShowNewAddressForm(true);
                    setSelectedAddressId('new');
                }
            } catch (error) {
                console.error('Error fetching addresses:', error);
                setShowNewAddressForm(true);
                setSelectedAddressId('new');
            }
        };
        
        if (isAuthenticated) {
            fetchAddresses();
        }
    }, [isAuthenticated]);

    // Fetch shipping rates when address changes
    const fetchShippingRates = useCallback(async () => {
        if (!selectedAddress || !selectedAddress.state) return;
        
        setLoadingShipping(true);
        setShippingRates([]);
        setSelectedShipping(null);
        
        try {
            // Get first vendor's location as origin (simplified - in real app might need multiple shipments)
            const originState = cartItems[0]?.vendor?.state || 'Lagos';
            
            const response = await shippingApi.getQuotes({
                origin_state: originState,
                destination_state: selectedAddress.state,
                destination_city: selectedAddress.city,
                destination_address: selectedAddress.address,
                weight: cartItems.reduce((acc, item) => acc + (item.weight || 1) * item.quantity, 0),
                shipbubble_address_code: selectedAddress.shipbubble_address_code || '',
                vendor: cartItems[0]?.vendor || null,
                category: cartItems[0]?.category || null
            });
            
            const rates = response.data?.data || response.data || [];
            setShippingRates(rates);
            
            // Auto-select cheapest option
            if (rates.length > 0) {
                const cheapest = rates.reduce((min, rate) => 
                    rate.price < min.price ? rate : min
                , rates[0]);
                setSelectedShipping(cheapest);
            }
        } catch (error) {
            console.error('Error fetching shipping rates:', error);
            toast.error('Failed to fetch shipping rates');
        } finally {
            setLoadingShipping(false);
        }
    }, [selectedAddress, cartItems]);

    useEffect(() => {
        if (currentStep === 2 && selectedAddress?.state) {
            fetchShippingRates();
        }
    }, [currentStep, selectedAddress?.state, fetchShippingRates]);

    const formatPrice = (price) => {
        return `₦${parseFloat(price || 0).toLocaleString('en-NG', { minimumFractionDigits: 2 })}`;
    };

    const handleAddressChange = (e) => {
        const { name, value, type, checked } = e.target;
        setNewAddress(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleSaveAddress = async () => {
        try {
            const response = await shippingApi.addAddress(newAddress);
            const savedAddr = response.data?.data || response.data;
            setSavedAddresses(prev => [...prev, savedAddr]);
            setSelectedAddressId(savedAddr.id);
            setShowNewAddressForm(false);
            toast.success('Address saved');
        } catch (error) {
            console.error('Error saving address:', error);
            toast.error('Failed to save address');
        }
    };

    const validateStep = (step) => {
        switch (step) {
            case 1:
                return selectedAddress && selectedAddress.address && selectedAddress.city && selectedAddress.state && selectedAddress.phone;
            case 2:
                return selectedShipping !== null;
            case 3:
                return paymentMethod !== null;
            case 4:
                return true;
            default:
                return false;
        }
    };

    const handleNext = () => {
        if (!validateStep(currentStep)) {
            toast.error('Please complete all required fields');
            return;
        }
        
        if (currentStep < 4) {
            setCurrentStep(currentStep + 1);
            window.scrollTo(0, 0);
        }
    };

    const handleBack = () => {
        if (currentStep > 1) {
            setCurrentStep(currentStep - 1);
            window.scrollTo(0, 0);
        }
    };

    const handlePlaceOrder = async () => {
        if (!validateStep(1) || !validateStep(2) || !validateStep(3)) {
            toast.error('Please complete all steps');
            return;
        }
        
        setIsProcessing(true);
        
        try {
            // Create order
            const orderData = {
                items: cartItems.map(item => ({
                    product_id: item.id,
                    quantity: item.quantity,
                    unit_price: item.price
                })),
                shipping_address: selectedAddress.address,
                shipping_city: selectedAddress.city,
                shipping_state: selectedAddress.state,
                shipping_postal_code: selectedAddress.postal_code || '',
                shipping_phone: selectedAddress.phone,
                recipient_name: selectedAddress.recipient_name,
                subtotal: subtotal,
                shipping_cost: shippingCost,
                total: total,
                notes: orderNotes,
                shipping_option: {
                    provider: selectedShipping.provider,
                    provider_type: selectedShipping.provider_type,
                    rate_id: selectedShipping.rate_id,
                    price: selectedShipping.price,
                    delivery_speed: selectedShipping.delivery_speed,
                    courier_id: selectedShipping.courier_id
                },
                payment_method: paymentMethod
            };
            
            const response = await ordersApi.createOrder(orderData);
            const order = response.data?.data || response.data;
            
            if (paymentMethod === 'paystack') {
                // Initialize Paystack payment
                const paystack = new Paystack();
                paystack.newTransaction({
                    key: window._env_?.REACT_APP_PAYSTACK_PUBLIC_KEY || 'pk_test_491a5759f99890d1de79e4984cd7757ffc3d5736',
                    email: user.email,
                    amount: Math.round(total * 100),
                    currency: 'NGN',
                    ref: `eaquahealth-${order.order_number || order.id}-${Date.now()}`,
                    metadata: {
                        order_id: order.id,
                        order_number: order.order_number,
                        custom_fields: [
                            {
                                display_name: "Order Number",
                                variable_name: "order_number",
                                value: order.order_number
                            }
                        ]
                    },
                    onSuccess: async (transaction) => {
                        try {
                            await ordersApi.verifyPayment(order.id, transaction.reference);
                            clearCart();
                            toast.success('Payment successful! Order placed.');
                            navigate(`/orders/${order.id}/confirmation`);
                        } catch (error) {
                            console.error('Payment verification error:', error);
                            toast.error('Payment verification failed. Please contact support.');
                        }
                    },
                    onCancel: () => {
                        toast.info('Payment cancelled');
                        setIsProcessing(false);
                    }
                });
            } else {
                // Handle other payment methods
                clearCart();
                toast.success('Order placed successfully!');
                navigate(`/orders/${order.id}/confirmation`);
            }
        } catch (error) {
            console.error('Order error:', error);
            toast.error(error.response?.data?.message || 'Failed to place order');
            setIsProcessing(false);
        }
    };

    const renderStepIndicator = () => (
        <div className="checkout-steps">
            {STEPS.map((step, index) => (
                <div key={step}
                    className={`step ${currentStep > index + 1 ? 'completed' : ''} ${currentStep === index + 1 ? 'active' : ''}`}
                    onClick={() => currentStep > index + 1 && setCurrentStep(index + 1)}
                >
                    <div className="step-number">
                        {currentStep > index + 1 ? <img src={Check} alt="" width={16} height={16} /> : index + 1}
                    </div>
                    <span className="step-name">{step}</span>
                    {index < STEPS.length - 1 && <img src={ChevronRight} alt="" width={16} height={16} className="step-arrow" />}
                </div>
            ))}
        </div>
    );

    const renderShippingStep = () => (
        <div className="checkout-section">
            <h2><img src={MapPin} alt="" width={24} height={24} /> Shipping Address</h2>
            
            {savedAddresses.length > 0 && (
                <div className="saved-addresses">
                    {savedAddresses.map(addr => (
                        <div 
                            key={addr.id}
                            className={`address-card ${selectedAddressId === addr.id ? 'selected' : ''}`}
                            onClick={() => {
                                setSelectedAddressId(addr.id);
                                setShowNewAddressForm(false);
                            }}
                        >
                            <div className="address-radio">
                                <input 
                                    type="radio" 
                                    checked={selectedAddressId === addr.id}
                                    onChange={() => {}}
                                />
                            </div>
                            <div className="address-content">
                                <div className="address-label">
                                    {addr.label}
                                    {addr.is_default && <span className="default-badge">Default</span>}
                                </div>
                                <div className="address-name">{addr.recipient_name}</div>
                                <div className="address-detail">{addr.address}</div>
                                <div className="address-detail">{addr.city}, {addr.state}</div>
                                <div className="address-phone">{addr.phone}</div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
            
            <button 
                className="btn-add-address"
                onClick={() => {
                    setShowNewAddressForm(true);
                    setSelectedAddressId('new');
                }}
            >
                <img src={Add} alt="" width={18} height={18} />
                Add New Address
            </button>
            
            {showNewAddressForm && (
                <div className="new-address-form">
                    <div className="form-row">
                        <FormInput
                            label="Label"
                            name="label"
                            value={newAddress.label}
                            onChange={handleAddressChange}
                            placeholder="e.g., Home, Office"
                        />
                        <FormInput
                            label="Recipient Name"
                            name="recipient_name"
                            value={newAddress.recipient_name}
                            onChange={handleAddressChange}
                            placeholder="Full name"
                            required
                        />
                    </div>
                    
                    <PhoneInput
                        label="Phone Number"
                        name="phone"
                        value={newAddress.phone}
                        onChange={handleAddressChange}
                        required
                    />
                    
                    <div className="form-group">
                        <label>Address</label>
                        <textarea
                            name="address"
                            value={newAddress.address}
                            onChange={handleAddressChange}
                            placeholder="Street address, apartment, suite, etc."
                            rows={3}
                            required
                        />
                    </div>
                    
                    <div className="form-row">
                        <FormInput
                            label="City"
                            name="city"
                            value={newAddress.city}
                            onChange={handleAddressChange}
                            placeholder="City"
                            required
                        />
                        <div className="form-group">
                            <label>State</label>
                            <select
                                name="state"
                                value={newAddress.state}
                                onChange={handleAddressChange}
                                required
                            >
                                <option value="">Select State</option>
                                {NIGERIAN_STATES.map(state => (
                                    <option key={state} value={state}>{state}</option>
                                ))}
                            </select>
                        </div>
                    </div>
                    
                    <FormInput
                        label="Postal Code (Optional)"
                        name="postal_code"
                        value={newAddress.postal_code}
                        onChange={handleAddressChange}
                        placeholder="Postal code"
                    />
                    
                    <label className="checkbox-label">
                        <input
                            type="checkbox"
                            name="is_default"
                            checked={newAddress.is_default}
                            onChange={handleAddressChange}
                        />
                        <span>Set as default address</span>
                    </label>
                    
                    <button 
                        type="button"
                        className="btn-save-address"
                        onClick={handleSaveAddress}
                    >
                        Save Address
                    </button>
                </div>
            )}
        </div>
    );

    const renderDeliveryStep = () => (
        <div className="checkout-section">
            <h2><img src={Truck} alt="" width={24} height={24} /> Delivery Options</h2>
            
            {selectedAddress && (
                <div className="delivery-to">
                    <span>Delivering to:</span>
                    <strong>{selectedAddress.city}, {selectedAddress.state}</strong>
                </div>
            )}
            
            {loadingShipping ? (
                <div className="shipping-loading">
                    <img src={Loader} alt="" width={24} height={24} className="spin" />
                    <p>Fetching available shipping options...</p>
                </div>
            ) : shippingRates.length > 0 ? (
                <div className="shipping-options">
                    {shippingRates.map((rate, index) => (
                        <div 
                            key={`${rate.provider}-${rate.rate_id || index}`}
                            className={`shipping-option ${selectedShipping?.rate_id === rate.rate_id && selectedShipping?.provider === rate.provider ? 'selected' : ''}`}
                            onClick={() => setSelectedShipping(rate)}
                        >
                            <div className="option-radio">
                                <input 
                                    type="radio" 
                                    checked={selectedShipping?.rate_id === rate.rate_id && selectedShipping?.provider === rate.provider}
                                    onChange={() => {}}
                                />
                            </div>
                            
                            <div className="option-logo">
                                {rate.company_logo ? (
                                    <img src={rate.company_logo} alt={rate.provider} />
                                ) : (
                                    <img src={Truck} alt="" width={32} height={32} />
                                )}
                            </div>
                            
                            <div className="option-details">
                                <div className="option-name">
                                    {rate.name}
                                    {rate.provider_type === 'shipbubble' && (
                                        <span className="provider-badge">via Shipbubble</span>
                                    )}
                                </div>
                                <div className="option-delivery">
                                    <img src={Clock} alt="" width={14} height={14} />
                                    {rate.min_delivery_days === rate.max_delivery_days 
                                        ? `${rate.min_delivery_days} day${rate.min_delivery_days > 1 ? 's' : ''}`
                                        : `${rate.min_delivery_days}-${rate.max_delivery_days} days`
                                    }
                                </div>
                                {rate.courier_name && (
                                    <div className="option-courier">Carrier: {rate.courier_name}</div>
                                )}
                            </div>
                            
                            <div className="option-price">
                                {formatPrice(rate.price)}
                            </div>
                        </div>
                    ))}
                </div>
            ) : (
                <div className="no-shipping">
                    <p>No shipping options available for this location.</p>
                    <button onClick={fetchShippingRates}>Try Again</button>
                </div>
            )}
        </div>
    );

    const renderPaymentStep = () => (
        <div className="checkout-section">
            <h2><img src={CreditCard} alt="" width={24} height={24} /> Payment Method</h2>
            
            <div className="payment-options">
                <div 
                    className={`payment-option ${paymentMethod === 'paystack' ? 'selected' : ''}`}
                    onClick={() => setPaymentMethod('paystack')}
                >
                    <div className="option-radio">
                        <input 
                            type="radio" 
                            checked={paymentMethod === 'paystack'}
                            onChange={() => {}}
                        />
                    </div>
                    <div className="option-content">
                        <div className="option-name">Pay with Paystack</div>
                        <div className="option-description">
                            Pay securely with your debit/credit card, bank transfer, or USSD
                        </div>
                        <div className="payment-badges">
                            <span>Visa</span>
                            <span>Mastercard</span>
                            <span>Bank Transfer</span>
                            <span>USSD</span>
                        </div>
                    </div>
                </div>
                
                {/* <div 
                    className={`payment-option ${paymentMethod === 'pay_on_delivery' ? 'selected' : ''}`}
                    onClick={() => setPaymentMethod('pay_on_delivery')}
                >
                    <div className="option-radio">
                        <input 
                            type="radio" 
                            checked={paymentMethod === 'pay_on_delivery'}
                            onChange={() => {}}
                        />
                    </div>
                    <div className="option-content">
                        <div className="option-name">Pay on Delivery</div>
                        <div className="option-description">
                            Pay with cash when your order is delivered (available in select locations)
                        </div>
                    </div>
                </div> */}
            </div>
            
            <div className="security-note">
                <img src={Shield} alt="" width={18} height={18} />
                <span>Your payment information is encrypted and secure</span>
            </div>
        </div>
    );

    const renderReviewStep = () => (
        <div className="checkout-section">
            <h2><img src={Package} alt="" width={24} height={24} /> Review Your Order</h2>
            
            <div className="review-sections">
                <div className="review-block">
                    <h3>Shipping Address</h3>
                    <p>{selectedAddress?.recipient_name}</p>
                    <p>{selectedAddress?.address}</p>
                    <p>{selectedAddress?.city}, {selectedAddress?.state}</p>
                    <p>{selectedAddress?.phone}</p>
                    <button className="btn-edit" onClick={() => setCurrentStep(1)}>Edit</button>
                </div>
                
                <div className="review-block">
                    <h3>Delivery Method</h3>
                    <p>{selectedShipping?.name}</p>
                    <p className="delivery-time">
                        <img src={Clock} alt="" width={14} height={14} />
                        {selectedShipping?.min_delivery_days}-{selectedShipping?.max_delivery_days} business days
                    </p>
                    <p className="shipping-cost">{formatPrice(selectedShipping?.price)}</p>
                    <button className="btn-edit" onClick={() => setCurrentStep(2)}>Edit</button>
                </div>
                
                <div className="review-block">
                    <h3>Payment Method</h3>
                    <p>{paymentMethod === 'paystack' ? 'Pay with Paystack' : 'Pay on Delivery'}</p>
                    <button className="btn-edit" onClick={() => setCurrentStep(3)}>Edit</button>
                </div>
            </div>
            
            <div className="order-items">
                <h3>Order Items ({cartItems.length})</h3>
                {cartItems.map(item => (
                    <div key={item.id} className="review-item">
                        <img 
                            src={item.image || item.images?.[0]?.image || prod} 
                            alt={item.name}
                            onError={(e) => { e.target.src = prod; }}
                        />
                        <div className="item-details">
                            <div className="item-name">{item.name}</div>
                            <div className="item-qty">Qty: {item.quantity}</div>
                        </div>
                        <div className="item-price">
                            {formatPrice(item.price * item.quantity)}
                        </div>
                    </div>
                ))}
            </div>
            
            <div className="form-group">
                <label>Order Notes (Optional)</label>
                <textarea
                    value={orderNotes}
                    onChange={(e) => setOrderNotes(e.target.value)}
                    placeholder="Add any special instructions for your order..."
                    rows={3}
                />
            </div>
        </div>
    );

    const renderCurrentStep = () => {
        switch (currentStep) {
            case 1:
                return renderShippingStep();
            case 2:
                return renderDeliveryStep();
            case 3:
                return renderPaymentStep();
            case 4:
                return renderReviewStep();
            default:
                return null;
        }
    };

    if (!isAuthenticated || cartItems.length === 0) {
        return (
            <div className="checkout-page">
                <div className="checkout-container" style={{ padding: '40px', textAlign: 'center' }}>
                    <img src={Loader} alt="" width={32} height={32} className="spin" />
                    <p>Loading...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="checkout-page">
            <div className="checkout-container">
                <div className="checkout-header">
                    <Link to="/cart" className="btn-back">
                        <img src={Back} alt="" width={18} height={18} />
                        Back to Cart
                    </Link>
                    <h1>Checkout</h1>
                </div>
                
                {renderStepIndicator()}
                
                <div className="checkout-layout">
                    <div className="checkout-main">
                        {renderCurrentStep()}
                        
                        <div className="checkout-navigation">
                            {currentStep > 1 && (
                                <button className="btn-back-step" onClick={handleBack}>
                                    <img src={Back} alt="" width={18} height={18} />
                                    Back
                                </button>
                            )}
                            
                            {currentStep < 4 ? (
                                <button 
                                    className="btn-next"
                                    onClick={handleNext}
                                    disabled={!validateStep(currentStep)}
                                >
                                    Continue
                                    <img src={ChevronRight} alt="" width={18} height={18} />
                                </button>
                            ) : (
                                <button 
                                    className="btn-place-order"
                                    onClick={handlePlaceOrder}
                                    disabled={isProcessing}
                                >
                                    {isProcessing ? (
                                        <>
                                            <img src={Loader} alt="" width={18} height={18} className="spin" />
                                            Processing...
                                        </>
                                    ) : (
                                        <>
                                            Place Order
                                            <img src={Check} alt="" width={18} height={18} />
                                        </>
                                    )}
                                </button>
                            )}
                        </div>
                    </div>
                    
                    <div className="checkout-sidebar">
                        <div className="order-summary">
                            <h2>Order Summary</h2>
                            
                            <div className="summary-items">
                                {cartItems.slice(0, 3).map(item => (
                                    <div key={item.id} className="summary-item">
                                        <div className="item-image">
                                            <img 
                                                src={item.image || item.images?.[0]?.image || prod} 
                                                alt={item.name}
                                                onError={(e) => { e.target.src = prod; }}
                                            />
                                            <span className="item-qty-badge">{item.quantity}</span>
                                        </div>
                                        <div className="item-info">
                                            <span className="item-name">{item.name}</span>
                                            <span className="item-price">{formatPrice(item.price)}</span>
                                        </div>
                                    </div>
                                ))}
                                {cartItems.length > 3 && (
                                    <div className="more-items">
                                        +{cartItems.length - 3} more items
                                    </div>
                                )}
                            </div>
                            
                            <div className="summary-divider"></div>
                            
                            <div className="summary-row">
                                <span>Subtotal</span>
                                <span>{formatPrice(subtotal)}</span>
                            </div>
                            
                            <div className="summary-row">
                                <span>Shipping</span>
                                <span>
                                    {selectedShipping ? formatPrice(shippingCost) : 'Calculated next'}
                                </span>
                            </div>
                            
                            <div className="summary-divider"></div>
                            
                            <div className="summary-row total">
                                <span>Total</span>
                                <span>{formatPrice(total)}</span>
                            </div>
                        </div>
                        
                        <div className="checkout-guarantees">
                            <div className="guarantee">
                                <img src={Shield} alt="" width={20} height={20} />
                                <span>Secure Payment</span>
                            </div>
                            <div className="guarantee">
                                <img src={Truck} alt="" width={20} height={20} />
                                <span>Fast Delivery</span>
                            </div>
                            <div className="guarantee">
                                <img src={Package} alt="" width={20} height={20} />
                                <span>Quality Products</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CheckoutPage;
